import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UploadImage } from 'app/interfaces/upload-images.interface';
import { EmpresaService } from './empresa.service';

@Injectable({
  providedIn: 'root',
})
export class ArchivosService {
  constructor(private http: HttpClient, private empresaService: EmpresaService) {}

  getArticulosExcel() {
    return this.http.get(environment.apiUrl + 'articles/download/excel', {
      responseType: 'blob' as 'json',
    });
  }

  getClientesExcel() {
    return this.http.get(environment.apiUrl + 'client/download/excel', {
      responseType: 'blob' as 'json',
    });
  }

  importarArchivoExcel(file: File) {
    let formData = new FormData();
    formData.append('file', file);
    return this.http.post(environment.apiUrlUpload, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  subirImagen(imagen: File): Promise<UploadImage> {
    return new Promise((resolve, reject) => {
      // Obtener información de la empresa primero
      this.empresaService.getEmpresaConfig().subscribe({
        next: empresa => {
          // Usar fantasyName si existe, sino socialReason, sino valor por defecto
          const nombreEmpresa = empresa?.fantasyName || empresa?.socialReason || 'default';
          const proyectoId = this.sanitizeProjectId(nombreEmpresa);
          this.uploadFileWithProjectId(imagen, proyectoId, resolve, reject);
        },
        error: err => {
          console.warn('No se pudo obtener información de la empresa, usando valor por defecto:', err);
          // Si falla obtener la empresa, usar valor por defecto
          this.uploadFileWithProjectId(imagen, 'default', resolve, reject);
        },
      });
    });
  }

  /**
   * Sanitiza el nombre de la empresa para usarlo como proyectoId
   * Remueve caracteres especiales, espacios, y convierte a minúsculas
   */
  private sanitizeProjectId(nombre: string): string {
    if (!nombre || nombre.trim() === '') {
      return 'default';
    }

    return (
      nombre
        .trim() // Eliminar espacios al inicio y final
        .toLowerCase() // Convertir a minúsculas
        .normalize('NFD') // Normalizar caracteres acentuados
        .replace(/[\u0300-\u036f]/g, '') // Remover acentos
        .replace(/[^a-z0-9\s-]/g, '') // Solo letras, números, espacios y guiones
        .replace(/\s+/g, '-') // Reemplazar espacios por guiones
        .replace(/-+/g, '-') // Reemplazar múltiples guiones por uno solo
        .replace(/^-+|-+$/g, '') // Remover guiones al inicio y final
        .substring(0, 50) || 'default'
    ); // Limitar longitud y fallback
  }

  /**
   * Función auxiliar para subir archivo con proyectoId específico
   */
  private uploadFileWithProjectId(
    imagen: File,
    proyectoId: string,
    resolve: (value: UploadImage) => void,
    reject: (reason?: any) => void
  ): void {
    let formData = new FormData();
    formData.append('file', imagen);
    formData.append('proyectoId', proyectoId);

    this.http.post<UploadImage>(environment.apiUrlUpload, formData).subscribe(
      res => {
        resolve(res);
      },
      err => {
        reject(err);
      }
    );
  }

  procesarArchivoExcel(nombreFile: string) {
    return this.http.post(environment.apiUrl + 'articulos/importar/excel', {
      nombreFile,
    });
  }

  procesarArchivoExcelClientes(fileUrl: string) {
    console.log(fileUrl);
    return this.http.post(environment.apiUrl + 'client/import/excel', {
      fileUrl,
    });
  }

  /**
   * Sube un archivo Excel con productos para carga masiva (deprecated)
   */
  uploadArticulosExcel(formData: FormData): Observable<any> {
    return this.http.post(environment.apiUrl + 'articles/import/excel', formData);
  }

  /**
   * Obtiene el preview de un archivo Excel antes de procesarlo
   */
  getArticulosExcelPreview(formData: FormData): Observable<any> {
    return this.http.post(environment.apiUrl + 'articles/upload/excel/preview', formData);
  }

  /**
   * Procesa el archivo Excel y carga los productos al sistema
   * Recibe el archivo Excel directamente (FormData) igual que el preview
   */
  processArticulosExcel(formData: FormData): Observable<any> {
    return this.http.post(environment.apiUrl + 'articles/upload/excel/process', formData);
  }
}
